import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, RefreshCw, Loader2, ChevronDown, MessageCircle } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';

interface Funnel {
  visitors: { page_view: number; add_to_cart: number; checkout_start: number; ordered: number; paid: number };
  rates: { cart_rate: number | null; checkout_rate: number | null; order_rate: number | null; paid_rate: number | null };
  window_days: number;
}
interface Timing { samples: number; converted_visitors: number; abandoned_visitors: number; median_hours: number | null; p90_hours: number | null; note?: string }
interface OfferChain { claims: number; redeemed: number; redeem_rate: number | null; repurchased: number; repurchase_rate: number | null; note?: string }
interface GiftBehavior { shipped: number; shared: number; share_rate: number | null; shared_buyers: number; unshared_buyers: number; note?: string }
interface Segments {
  high_intent_no_order: { count: number };
  offer_hunters: { count: number; phones: string[] };
  dormant: { count: number; phones: string[] };
  active_buyers: { count: number };
}
interface ChannelCompare { wa_main: { inbound: number; outbound: number; replied_conversations: number }; wa_ai: Record<string, unknown> }
interface Snapshot { date: string; orders: number; revenue_cents: number; new_customers: number; wa_inbound: number }

interface PatternsData {
  computed_at: number;
  patterns: {
    funnel: Funnel | null;
    timing: Timing | null;
    offer_chain: OfferChain | null;
    gift_behavior: GiftBehavior | null;
    segments: Segments | null;
    channel_compare: ChannelCompare | null;
  };
  snapshots: Snapshot[];
}

const pct = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${v}%`);

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <h2 className="mb-3 text-sm font-bold tracking-wide text-red-600">{title}</h2>
      {children}
    </div>
  );
}

function Suggest({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
      💡 {children}
    </p>
  );
}

interface CorpusItem {
  id: number;
  phone: string;
  customer_name: string;
  question: string;
  answer: string;
  category: string;
  week: string;
}
interface CorpusStats { total: number; this_week: number; by_category: { category: string; n: number }[] }

const AdminPatterns: React.FC = () => {
  const [tab, setTab] = useState<'patterns' | 'corpus'>('patterns');
  const [corpus, setCorpus] = useState<{ stats: CorpusStats; items: CorpusItem[] } | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const navigate = useNavigate();
  const [data, setData] = useState<PatternsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/public/admin/patterns');
      if (res.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin'); return; }
      const json = await res.json();
      setData(json.data ?? json);
    } finally {
      setLoading(false);
    }
  };

  const fetchCorpus = async () => {
    try {
      const res = await apiFetch('/api/public/admin/corpus');
      if (res.ok) setCorpus((await res.json()).data ?? (await res.json()));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) { navigate('/admin'); return; }
    fetchData();
    fetchCorpus();
  }, []);

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      await apiFetch('/api/public/admin/patterns/recompute', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      await fetchData();
    } finally {
      setRecomputing(false);
    }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) { localStorage.removeItem('admin_token'); navigate('/admin'); }
  };

  const p = data?.patterns;
  const maxSnap = Math.max(1, ...(data?.snapshots || []).map((s) => s.wa_inbound));

  return (
    <AdminLayout currentPage="patterns" onLogout={handleLogout}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-800 dark:text-white">
              <TrendingUp size={26} className="text-red-600" /> 行為模式
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              客戶行為聚合洞見——每週一 09:00 自動更新（急用可即時重算）{data?.computed_at ? `｜最後計算：${new Date(data.computed_at * 1000).toLocaleString('zh-HK')}` : ''}
            </p>
          </div>
          <button onClick={async () => { await handleRecompute(); await fetchCorpus(); }} disabled={recomputing} className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">
            {recomputing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} 立即重算
          </button>
        </div>

        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
          {([['patterns', '📈 行為模式'], ['corpus', '📚 語料庫']] as const).map(([t, label]) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px transition ${tab === t ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
            >
              {label}{t === 'corpus' && corpus ? `（${corpus.stats.total}）` : ''}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500 dark:border-gray-700 dark:bg-gray-800">載入中...</div>
        ) : !data ? (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500 dark:border-gray-700 dark:bg-gray-800">載入失敗，請重試</div>
        ) : tab === 'patterns' ? (
          <>
            {/* P1 漏斗 */}
            <Card title="轉化漏斗（近 30 日）">
              {p?.funnel ? (
                <>
                  <div className="grid grid-cols-5 gap-2 text-center">
                    {[['瀏覽', p.funnel.visitors.page_view], ['加購', p.funnel.visitors.add_to_cart], ['結帳', p.funnel.visitors.checkout_start], ['落單', p.funnel.visitors.ordered], ['付款', p.funnel.visitors.paid]].map(([label, n], i) => (
                      <div key={i} className="rounded-lg bg-gray-50 p-2 dark:bg-gray-900">
                        <p className="text-lg font-bold text-gray-800 dark:text-white">{String(n)}</p>
                        <p className="text-[11px] text-gray-500">{label}</p>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    加購 {pct(p.funnel.rates.cart_rate)} → 結帳 {pct(p.funnel.rates.checkout_rate)} → 落單 {pct(p.funnel.rates.order_rate)} → 付款 {pct(p.funnel.rates.paid_rate)}
                  </p>
                  <Suggest>{(() => {
                    const r = p.funnel.rates;
                    const weakest = [['加購→結帳', r.checkout_rate, '考慮向加購客發結帳協助訊息（CRM 篩選）'], ['結帳→落單', r.order_rate, '結帳頁可能有障礙——檢查流程或發跟進'], ['落單→付款', r.paid_rate, '付款障礙或意猶未盡——發付款提醒／協助']].filter(([, v]) => v !== null).sort((a, b) => Number(a[1]) - Number(b[1]))[0];
                    return weakest ? `最弱一環：${weakest[0]}（${weakest[1]}%）→ ${weakest[2]}` : '樣本累積中';
                  })()}</Suggest>
                </>
              ) : <p className="text-sm text-gray-400">—</p>}
            </Card>

            {/* P2 時差 */}
            <Card title="轉化時差（加購 → 落單）">
              {p?.timing ? (
                <>
                  <p className="text-sm text-gray-700 dark:text-gray-200">
                    加購訪客 <b>{p.timing.samples}</b> 人 · 已轉化 <b>{p.timing.converted_visitors}</b> · 未轉化 <b>{p.timing.abandoned_visitors}</b>
                  </p>
                  {p.timing.median_hours !== null && (
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">
                      中位數 <b>{p.timing.median_hours} 小時</b> · 90 分位 <b>{p.timing.p90_hours} 小時</b>
                    </p>
                  )}
                  <Suggest>{p.timing.median_hours ? `大部分客加購後 ${p.timing.median_hours} 小時內決定——追單訊息宜喺呢個時點前出，唔好早過佢` : '時差樣本累積中'}</Suggest>
                </>
              ) : <p className="text-sm text-gray-400">—</p>}
            </Card>

            {/* P3 優惠鏈路 */}
            <Card title="優惠鏈路（近 60 日）">
              {p?.offer_chain ? (
                <>
                  <p className="text-sm text-gray-700 dark:text-gray-200">
                    領取 <b>{p.offer_chain.claims}</b> · 兌換 <b>{p.offer_chain.redeemed}</b>（{pct(p.offer_chain.redeem_rate)}）→ 回購 <b>{p.offer_chain.repurchased}</b>（{pct(p.offer_chain.repurchase_rate)}）
                  </p>
                  <Suggest>{p.offer_chain.redeem_rate !== null && p.offer_chain.redeem_rate < 50 ? '領取多兌換少——考慮到期前發提醒，或縮短有效期製造急迫感' : '兌換率健康——重點轉去「兌換後回購」：兌換者 30 日內跟進'}</Suggest>
                </>
              ) : <p className="text-sm text-gray-400">—</p>}
            </Card>

            {/* P4 禮品行為 */}
            <Card title="谷友禮品行為">
              {p?.gift_behavior ? (
                <>
                  <p className="text-sm text-gray-700 dark:text-gray-200">
                    已寄出 <b>{p.gift_behavior.shipped}</b> · 已分享 <b>{p.gift_behavior.shared}</b>（{pct(p.gift_behavior.share_rate)}）
                  </p>
                  <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">
                    分享者落單 <b>{p.gift_behavior.shared_buyers}</b> 人 vs 無分享者落單 <b>{p.gift_behavior.unshared_buyers}</b> 人
                  </p>
                  <Suggest>分享者消費高過無分享者＝谷友計劃有效實證（可用於商家談判）；T+2 提醒係拉 share_rate 嘅主力</Suggest>
                </>
              ) : <p className="text-sm text-gray-400">—</p>}
            </Card>

            {/* P5 客群分層 */}
            <Card title="客群分層（可執行名單）">
              {p?.segments ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  {([
                    ['🎯 高意圖未落單', p.segments.high_intent_no_order.count, '近 30 日有加購/結帳動作但未落單——追單黃金名單', null],
                    ['🏷️ 優惠獵人', p.segments.offer_hunters.count, '領取 ≥3 次零消費——要轉化策略唔好再送優惠', p.segments.offer_hunters.phones],
                    ['😴 沉睡客', p.segments.dormant.count, '曾消費但 30+ 日無動靜——喚醒訊息／優惠', p.segments.dormant.phones],
                    ['🔥 活躍買家', p.segments.active_buyers.count, '近 30 日有付款——回購推動／辣友卡推介', null],
                  ] as [string, number, string, string[] | null][]).map(([label, n, hint, phones]) => (
                    <div key={label} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                      <div className="flex items-baseline justify-between">
                        <p className="text-sm font-bold text-gray-800 dark:text-white">{label}</p>
                        <p className="text-xl font-bold text-red-600">{n}</p>
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
                      {phones && phones.length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-xs text-red-600">📱 開名單（前 20）</summary>
                          <div className="mt-1 max-h-40 overflow-y-auto">
                            {phones.map((ph) => (
                              <button key={ph} onClick={() => navigate('/admin/conversations')} className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-left font-mono text-xs text-gray-600 hover:bg-red-50 dark:text-gray-300 dark:hover:bg-gray-700">
                                <MessageCircle size={11} className="text-red-500" /> {ph}
                              </button>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-gray-400">—</p>}
            </Card>

            {/* P6 通道對比 */}
            <Card title="通道對比（近 30 日）">
              {p?.channel_compare ? (
                <>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-900">
                      <p className="text-xs font-bold text-gray-500">📱 主號（人手）</p>
                      <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">入 {p.channel_compare.wa_main.inbound} · 出 {p.channel_compare.wa_main.outbound} · 有回覆對話 {p.channel_compare.wa_main.replied_conversations}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-900">
                      <p className="text-xs font-bold text-gray-500">🤖 AI 號碼</p>
                      <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">
                        {'note' in p.channel_compare.wa_ai ? '未啟用' : `入 ${(p.channel_compare.wa_ai as any).inbound} · 出 ${(p.channel_compare.wa_ai as any).outbound}`}
                      </p>
                    </div>
                  </div>
                  <Suggest>AI 號碼啟用後，呢度就係「人工 vs AI」表現對比基準——回應量/覆蓋直接比</Suggest>
                </>
              ) : <p className="text-sm text-gray-400">—</p>}
            </Card>

            {/* 趨勢 */}
            {(data.snapshots.length || 0) > 0 && (
              <Card title={`趨勢（${data.snapshots.length} 期快照）`}>
                <div className="flex items-end gap-1.5 overflow-x-auto pb-1" style={{ height: 110 }}>
                  {data.snapshots.map((s) => (
                    <div key={s.date} className="flex w-10 shrink-0 flex-col items-center justify-end gap-0.5" title={`${s.date}：${s.orders} 單 HK$${(s.revenue_cents / 100).toFixed(0)} · 入站 ${s.wa_inbound}`}>
                      <div className="w-6 rounded-t bg-red-500/80" style={{ height: `${Math.max(4, (s.wa_inbound / maxSnap) * 70)}px` }} />
                      <span className="text-[9px] text-gray-400">{s.date.slice(5)}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-gray-400">柱高＝WhatsApp 入站訊息數（每期快照）；懸浮睇該期訂單/營收</p>
              </Card>
            )}
          </>
        ) : tab === 'corpus' ? (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-xs text-gray-500 dark:text-gray-400">累積語料</p>
                <p className="mt-1 text-2xl font-bold text-gray-800 dark:text-white">{corpus?.stats.total ?? 0}<span className="text-sm font-normal text-gray-400"> 條</span></p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-xs text-gray-500 dark:text-gray-400">本週新增</p>
                <p className="mt-1 text-2xl font-bold text-gray-800 dark:text-white">{corpus?.stats.this_week ?? 0}<span className="text-sm font-normal text-gray-400"> 條</span></p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-xs text-gray-500 dark:text-gray-400">分類</p>
                <p className="mt-1 text-xs text-gray-700 dark:text-gray-200">{(corpus?.stats.by_category || []).map((c) => `${c.category} ${c.n}`).join(' · ') || '—'}</p>
                <a href="/api/public/admin/corpus/export" className="mt-1 inline-block text-xs font-bold text-red-600 hover:underline">⬇ 導出 JSONL</a>
              </div>
            </div>
            <p className="text-xs text-gray-400">每週一連同行為模式自動提煉——只收後台人手回覆（系統自動訊息唔入語料）；每條附行為調味（訂單/消費/標籤/會員）。</p>
            {(corpus?.items || []).length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500 dark:border-gray-700 dark:bg-gray-800">語料累積中——每週一自動提煉</div>
            ) : (
              <div className="space-y-2">
                {(corpus?.items || []).map((it) => (
                  <div key={it.id} className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
                    <button onClick={() => setExpanded(expanded === it.id ? null : it.id)} className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left">
                      <span className="min-w-0 truncate text-sm font-medium text-gray-800 dark:text-gray-100">Q：{it.question}</span>
                      <span className="shrink-0 text-[11px] text-gray-400">{it.category} · {it.week}</span>
                    </button>
                    {expanded === it.id && (
                      <div className="border-t border-gray-100 px-4 py-3 text-sm dark:border-gray-700">
                        <p className="text-xs text-gray-400">客人（{it.phone}）</p>
                        <p className="mt-1 whitespace-pre-wrap text-gray-700 dark:text-gray-200">{it.question}</p>
                        <p className="mt-2 text-xs text-gray-400">後台回覆</p>
                        <p className="mt-1 whitespace-pre-wrap text-gray-700 dark:text-gray-200">{it.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          null
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPatterns;
